//Dependencies
import React, { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
//Components From Dependency
// import Swal from "sweetalert2";
import { Button } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";

//Components
import { InstructionsTextSolicitudViolencia } from "./FormComponents/InstructionsTextSolicitudViolencia";
import { FirstFormSectionSolicitudViolencia } from "./FormComponents/FirstFormPartSolicitudViolencia";
import { SecondFormSectionSolicitudViolencia } from "./FormComponents/SecondFormSectionSolicitudViolencia";
import { BottomTextSolicitudViolencia } from "./FormComponents/BottomTextSolicitudViolencia";
import { ExitButtonSolicitudViolencia } from "./FormComponents/ExitButtonSolicitudViolencia";
import {
  PdfsSolicitudViolencia,
  SolicitudViolenciaData,
} from "../../../interfaces/solicitudViolencia/dataInterface";
import safeHTML from "../../../helpers/sanitizeObject";

//Utils
import { solicitudViolenciaSchema } from "../utils/solicitudViolenciaSchema";
import { preparedObjectSolicitudViolencia } from "../utils/formMethodsSolicitudViolencia";
import { postSolicitudViolencia } from "../../../services/solicitudViolencia/solicitudViolencia.service";
import { FileValidated } from "@dropzone-ui/react";
import Swal from "sweetalert2";

const SolicitudViolenciaForm = () => {
  const methods = useForm({
    defaultValues: {
      demandingName: "",
      demandingFirstLastName: "",
      demandingSecondLastName: "",
      demandingPhone: "",
      demandingEmail: "",
      demandingCurp: "",
      demandingDescription: "",
      demandingFiles: [],

      defendantName: "",
      defendantFirstLastName: "",
      defendantSecondLastName: "",
      defendantPhone: "",
      defendantAddress: "",
    },
    resolver: yupResolver(solicitudViolenciaSchema),
  });

  const { handleSubmit, reset, getValues } = methods;

  const fetchForm = async (
    safeData: SolicitudViolenciaData,
    method: number,
    filesBase64: any[],
    token: string
  ) => {
    const preparedData = preparedObjectSolicitudViolencia(
      safeData,
      method,
      filesBase64,
      token
    );
    await (window as any).Swal.close();
    Swal.fire({
      title: "Subiendo tu información",
      html: "Por favor espere...",
      allowOutsideClick: false,
      didOpen: () => {
        (window as any).Swal.showLoading();
      },
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
    });
    const response: any = await postSolicitudViolencia(preparedData);

    if (response.request.status === 201) {
      Swal.fire({
        title: "¡Éxito!",
        text: "Tu solicitud ha sido enviada exitosamente",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
      reset();
    } else {
      Swal.fire({
        title: "¡Error!",
        text: "Ha ocurrido un error al enviar tu solicitud, intentalo nuevamente",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    }
  };

  // Método para enviar la información después de validar el formulario, token y método de validación al backend
  // Al final del envió se borrara la información del formulario
  const submitForm = async (
    data: SolicitudViolenciaData,
    method: number,
    token: string
  ) => {
    Swal.fire({
      title: "Cargando y preparando tu información",
      html: "Por favor espere...",
      allowOutsideClick: false,
      didOpen: () => {
        (window as any).Swal.showLoading();
      },
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
    });

    const safeData = safeHTML<SolicitudViolenciaData>(data);

    let filesBase64: PdfsSolicitudViolencia[] = [];

    //Esta promesa sirve para esperar a que se procesen los archivos y se conviertan a base64
    // Finalizando se hace la petición al backend
    Promise.all(
      safeData.demandingFiles.map((file: FileValidated) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.file);
        const promise = new Promise((resolve, reject) => {
          reader.onload = () => {
            resolve(reader.result);
          };
        });
        return promise;
      })
    )
      .then((files64) => {
        for (let i = 0; i < files64.length; i++) {
          filesBase64.push({
            name: safeData.demandingFiles[i].file.name,
            content: files64[i] as string,
          });
        }
      })
      .then(async () => {
        await fetchForm(safeData, method, filesBase64, token);
      });
  };

  // Método para validar el formulario con el token de google, y elegir el método de validación del usuario.
  const verifyData = async (data: SolicitudViolenciaData) => {
    const token = await recaptchaRef?.current?.executeAsync();

    //Con esta alerta el usuario elige su método de validación
    const email = getValues("demandingEmail");
    await Swal.fire({
      title:
        "Para poder enviar la solicitud es necesario que confirmes el envió",
      showDenyButton: email ? true : false,
      showCancelButton: true,
      confirmButtonText: "Confirmar por WhatsApp",
      denyButtonText: `Confirmar por Correo`,
      cancelButtonText: "Cancelar",
      customClass: {
        confirmButton: "validator-method-whatsapp",
        denyButton: "validator-method-email",
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        const method = 1;
        submitForm(data, method, token as string);
      } else if (result.isDenied) {
        const method = 2;
        submitForm(data, method, token as string);
      } else if (result.isDismissed) {
        return;
      }
    });
  };

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  return (
    <FormProvider {...methods}>
      <form className="card__size mt-6 mb-6 m-auto p-8 relative flex flex-col">
        <InstructionsTextSolicitudViolencia />

        <FirstFormSectionSolicitudViolencia />
        <SecondFormSectionSolicitudViolencia />

        {process.env.REACT_APP_RECAPTCHA_SITE_KEY_SOLICITUD_VIOLENCIA && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={
              process.env.REACT_APP_RECAPTCHA_SITE_KEY_SOLICITUD_VIOLENCIA
            }
            size="invisible"
            badge="inline"
            className="flex justify-center w-full mt-5"
          />
        )}

        <Button
          className="send-button"
          variant="contained"
          sx={{ margin: "25px auto 0 auto" }}
          onClick={handleSubmit(verifyData)}
        >
          Enviar Información
        </Button>
        <BottomTextSolicitudViolencia />

        <ExitButtonSolicitudViolencia />
      </form>
    </FormProvider>
  );
};

export { SolicitudViolenciaForm };
